import type { ConversationStatus } from "./conversation";
import type { ClickedAd } from "./message";

export const ACTIVITY_TYPE = {
  CREATED: "created",
  ASSIGNED: "assigned",
  UNASSIGNED: "unassigned",
  LIFECYCLE_CHANGE: "lifecycle_change",
  CONVERSATION_STATUS_CHANGE: "conversation_status_change",
  SNOOZE: "snooze",
  UNSNOOZE: "unsnooze",
  BLOCK: "block",
  UNBLOCK: "unblock",
  NOTE: "note",
  ADS_CLICKED: "ads_clicked",
  WEBSITE_VISITED: "website_visited",
  FORM_SUBMITTED: "form_submitted",
  CALL: "call",
  ORDER_UPDATE: "order_update",
  FOLLOW_UP_SET: "follow_up_set",
  FOLLOW_UP_CLEARED: "follow_up_cleared",
  FOLLOW_UP_UPDATED: "follow_up_updated",
  FOLLOW_UP_COMPLETED: "follow_up_completed",
  APPOINTMENT_CREATED: "appointment_created",
  APPOINTMENT_UPDATED: "appointment_updated",
  APPOINTMENT_RESCHEDULED: "appointment_rescheduled",
  APPOINTMENT_DELETED: "appointment_deleted",
  // Tag activities
  TAG_ADDED: "tag_added",
  TAG_REMOVED: "tag_removed",
  // Workflow activities
  WORKFLOW_STARTED: "workflow_started",
  WORKFLOW_ENDED: "workflow_ended",
  WORKFLOW_PAUSED: "workflow_paused",
  WORKFLOW_DELAYED: "workflow_delayed",
  // Handover activities
  HANDOVER_PARKED: "handover_parked",
  HANDOVER_EXHAUSTED: "handover_exhausted",
} as const;

export type ActivityType = (typeof ACTIVITY_TYPE)[keyof typeof ACTIVITY_TYPE];

export const CALL_OUTCOME = {
  ANSWERED: "answered",
  MISSED: "missed",
  VOICEMAIL: "voicemail",
  BUSY: "busy",
  NO_ANSWER: "no_answer",
} as const;

export type CallOutcome = (typeof CALL_OUTCOME)[keyof typeof CALL_OUTCOME];

export const CALL_DIRECTION = {
  INBOUND: "inbound",
  OUTBOUND: "outbound",
  INCOMING: "incoming",
  OUTGOING: "outgoing",
} as const;

export type CallDirection =
  (typeof CALL_DIRECTION)[keyof typeof CALL_DIRECTION];

// Interfaces
interface BaseActivity {
  id: string;
  userId: string | null;
  contactId: string;
  mentioned: Array<string>;
  createdAt: string;
  owner?: "user" | "api" | "ai" | "system";
}

interface ActivityPayloadMap {
  assigned: AssigneeActivityPayload;
  unassigned: AssigneeActivityPayload;
  lifecycle_change: LifecycleActivityPayload;
  conversation_status_change: StatusActivityPayload;
  created: ContactCreatedActivityPayload;
  note: NoteActivityPayload;
  snooze: SnoozeActivityPayload;
  block: ContactBlockActivityPayload;
  unblock: ContactBlockActivityPayload;
  unsnooze: Record<string, never>;
  ads_clicked: ClickedAd;
  website_visited: WebsiteVisitedActivityPayload;
  form_submitted: FormSubmittedActivityPayload;
  call: CallActivityPayload;
  order_update: OrderUpdateActivityPayload;
  follow_up_set: FollowUpActivityPayload;
  follow_up_cleared: FollowUpActivityPayload;
  follow_up_updated: FollowUpUpdatedActivityPayload;
  follow_up_completed: FollowUpActivityPayload;
  appointment_created: AppointmentActivityPayload;
  appointment_updated: AppointmentUpdatedActivityPayload;
  appointment_rescheduled: AppointmentRescheduledActivityPayload;
  appointment_deleted: AppointmentActivityPayload;
  tag_added: TagActivityPayload;
  tag_removed: TagActivityPayload;
  workflow_started: WorkflowStartedPayload;
  workflow_ended: WorkflowEndedPayload;
  workflow_paused: WorkflowPausedPayload;
  workflow_delayed: WorkflowDelayedPayload;
  handover_parked: HandoverParkedPayload;
  handover_exhausted: HandoverExhaustedPayload;
}

export type SnoozeActivityPayload = {
  snoozedUntil: string;
};

export type AssigneeActivityPayload = {
  assignee: string;
  description?: string;
};

export type LifecycleActivityPayload = {
  lifecycleId: string;
  fromLifecycleId?: string;
  reason?: string;
};

export type ConversationStatusChangeReason = "follow_up_due";

export type StatusActivityPayload = {
  status: ConversationStatus;
  reason?: ConversationStatusChangeReason;
  closingNote?: {
    topicId: string;
    topicName: string;
    summary?: string;
    fieldValues?: Array<{
      fieldId: string;
      fieldName: string;
      type: string;
      value: Record<string, unknown>;
    }>;
  };
};

export type ContactCreatedActivityPayload = {
  userId?: string;
  // Audit note stamped when the contact originated from a channel, e.g.
  // "Synced from 'Acme WhatsApp'".
  description?: string;
};

export type NoteActivityPayload = {
  text: string;
};

export type ContactBlockActivityPayload = {
  blocked: boolean;
  userId: string;
};

export type CallTranscriptionLine = {
  speaker: "agent" | "customer";
  text: string;
  timestamp?: string;
};

export type CallActivityPayload = {
  direction: CallDirection;
  duration?: number;
  outcome?: CallOutcome;
  status?: CallOutcome;
  callerId?: string;
  recordingUrl?: string;
  notes?: string;
  transcription?: CallTranscriptionLine[];
};

export type OrderUpdateTarget = "delivery" | "payment";

export type OrderUpdateActivityPayload = {
  orderId: string;
  displayOrderId: string;
  target: OrderUpdateTarget;
  oldStatus: string | null;
  newStatus: string;
  createdAt: string;
};

export type WebsiteVisitedActivityPayload = {
  websiteId: string;
  websiteName: string;
  domain: string;
  url: string;
  pagePath?: string;
  pageTitle?: string;
  utmSource?: string;
  utmMedium?: string;
  utmCampaign?: string;
};

export type FormSubmissionAnswer = {
  question: string;
  value: unknown;
};

export type FormSubmissionWebsiteVisit = {
  url: string | null;
  pagePath: string | null;
  pageTitle: string | null;
  utmSource: string | null;
  utmMedium: string | null;
  utmCampaign: string | null;
  utmTerm: string | null;
  utmContent: string | null;
};

export type FormSubmittedActivityPayload = {
  submissionId: string;
  formId: string;
  formName: string;
  source?: string;
  externalId?: string | null;
  adId?: string | null;
  // Attribution captured at submission time: the ad the contact clicked and the
  // website visit that preceded the fill, so agents can see how the lead arrived.
  adName?: string | null;
  websiteVisitId?: string | null;
  websiteVisit?: FormSubmissionWebsiteVisit | null;
  // Legacy shape: older activities stored just the visit URL.
  websiteUrl?: string | null;
  fields: FormSubmissionAnswer[];
  submittedAt?: string;
};

export type FollowUpActivityPayload = {
  followUpAt: string | null;
  followUpReason: string | null;
  source?: "user" | "workflow" | "playbook" | "system" | "ai";
  skipWhatsapp?: boolean;
  archived?: boolean;
  archivedAt?: string;
};

export type FollowUpUpdatedActivityPayload = FollowUpActivityPayload & {
  oldFollowUpReason?: string | null;
  oldFollowUpAt?: string | null;
};

export type AppointmentActivityPayload = {
  appointmentId: string;
  startDate: string;
  endDate: string;
};

export type AppointmentChange = {
  field: string;
  previous?: unknown;
  new?: unknown;
};

export type AppointmentUpdatedActivityPayload = {
  appointmentId: string;
  changes: AppointmentChange[];
};

export type AppointmentRescheduledActivityPayload = {
  appointmentId: string;
  oldStartDate: string;
  oldEndDate: string;
  newStartDate: string;
  newEndDate: string;
  rescheduledCount: number;
};

export type TagRef = {
  id: string;
  name: string;
};

export type TagActivityPayload = {
  /** Tags paired with their names. `reason` is only set by AI auto-tagging. */
  tags?: TagRef[];
  reason?: string;
};

export type WorkflowStartedPayload = {
  workflowId: string;
  workflowName: string;
  executionId: string;
};

export type WorkflowEndedPayload = {
  workflowId: string;
  workflowName: string;
  executionId: string;
  reason: "success" | "exited" | "cancelled" | "failed";
  exitReason?: string;
};

export type WorkflowPausedPayload = {
  workflowId: string;
  workflowName: string;
  executionId: string;
};

export type WorkflowDelayedPayload = {
  workflowId: string;
  workflowName: string;
  executionId: string;
  reason: "timer" | "waiting_input" | "waiting_reply" | "waiting_contact_reply";
  resumeAt?: string;
};

export type HandoverParkedReason = "outside_hours" | "no_online_agent";

export type HandoverParkedPayload = {
  reason: HandoverParkedReason;
  triedUserIds: string[];
};

export type HandoverExhaustedPayload = {
  triedUserIds: string[];
  poolSource: string;
};

export type Activity = {
  [K in keyof ActivityPayloadMap]: BaseActivity & {
    type: K;
    payload: ActivityPayloadMap[K];
  };
}[keyof ActivityPayloadMap];

export type CreateActivityPayload = {
  [K in keyof ActivityPayloadMap]: Pick<
    BaseActivity,
    "contactId" | "mentioned"
  > & {
    id?: string;
    type: K;
    payload: ActivityPayloadMap[K];
  };
}[keyof ActivityPayloadMap];
